import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { rememberSignedInUser } from './lastUser';
import { ApiClient, api, toRole, type Role, type SessionGrant } from '@aithora/shared';
import { clearSession, currentToken, readSession, writeSession, type Session } from './store';
import { registerForPush, unregisterPush } from './push';

interface SessionValue {
  session: Session | null;
  /** True until a stored session has been checked with the server. */
  checking: boolean;
  client: ApiClient;
  /** Store the session the server just granted, then fill in the organisation from /auth/me. */
  signIn: (grant: SessionGrant) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * View as (Super Admin only): the role the console renders instead of your own, or
   * null. It never changes the session's role; while set, the console is read only and
   * the client refuses every write (see ApiClient getViewAs).
   */
  viewAs: Role | null;
  /**
   * Start or stop viewing as a role. Ignored unless the signed-in role is SUPER_ADMIN.
   * The server logs the start and end (POST /view-as/start|end) and answers every call
   * as that role; a facility role takes the facility picked (`facilityId`).
   */
  setViewAs: (role: Role | null, facility?: { id: string; name: string | null } | null) => void;
  /** The building being viewed as (a facility role), or null. */
  viewAsFacility: { id: string; name: string | null; demoBuilding?: boolean } | null;
}

/** Only a Super Admin may view the console as another role. */
export function mayViewAs(realRole: Role | null): boolean {
  return realRole === 'SUPER_ADMIN';
}

const Ctx = createContext<SessionValue | null>(null);

/** Base URL of the API. Empty = same origin (the dev proxy, or a Pages proxy in production). */
export const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

export function SessionProvider({ children, fetchImpl }: { children: ReactNode; fetchImpl?: typeof fetch }) {
  const [session, setSession] = useState<Session | null>(() => readSession());
  const [checking, setChecking] = useState<boolean>(() => readSession() !== null);
  // Kept in memory only: a reload is always back to your own role.
  const [viewAsState, setViewAsState] = useState<Role | null>(null);
  const [viewAsFacility, setViewAsFacility] = useState<{ id: string; name: string | null; demoBuilding?: boolean } | null>(null);
  const viewAs = mayViewAs(session?.user.role ?? null) && viewAsState !== session?.user.role ? viewAsState : null;
  const viewAsRef = useRef<Role | null>(null);
  viewAsRef.current = viewAs;

  const client = useMemo(
    () =>
      new ApiClient({
        baseUrl: API_BASE_URL,
        getToken: currentToken,
        fetch: fetchImpl,
        getViewAs: () => viewAsRef.current,
        // The server ended the session (expired, signed out elsewhere, account suspended).
        onUnauthorized: () => {
          clearSession();
          setSession(null);
        },
      }),
    [fetchImpl],
  );

  const refreshFromServer = useCallback(
    async (base: Session): Promise<Session | null> => {
      try {
        const { user } = await api.me(client);
        const next: Session = {
          ...base,
          user: {
            id: user.id,
            name: user.name,
            email: user.email,
            rawRole: user.role,
            role: toRole(user.role),
            organizationId: user.organization_id,
            orgName: user.org_name,
            demoBuilding: user.demo_building === true,
            lang: user.lang === 'es' || user.lang === 'en' ? user.lang : null,
            tutorialSeen: user.tutorial_seen === true,
            signinCount: typeof user.signin_count === 'number' ? user.signin_count : 0,
            timeZone: typeof user.time_zone === 'string' ? user.time_zone : null,
          },
        };
        writeSession(next);
        return next;
      } catch (err) {
        // 401 is handled by onUnauthorized. Anything else (offline, 500): keep the
        // session we have rather than signing the person out on a hiccup.
        if ((err as { kind?: string }).kind === 'unauthorized') return null;
        return base;
      }
    },
    [client],
  );

  // A session restored from sessionStorage is checked once with the server.
  useEffect(() => {
    const stored = readSession();
    if (!stored) {
      setChecking(false);
      return;
    }
    let live = true;
    refreshFromServer(stored).then((s) => {
      if (!live) return;
      setSession(s);
      setChecking(false);
      // A browser that already allowed push re-registers (the token may have changed);
      // nobody is asked for permission on a reload.
      if (s) void registerForPush(client, { prompt: false });
    });
    return () => {
      live = false;
    };
  }, [refreshFromServer, client]);

  const signIn = useCallback(
    async (grant: SessionGrant) => {
      rememberSignedInUser(grant.userId);
      const base: Session = {
        token: grant.token,
        expiresAt: grant.expiresAt ?? null,
        user: {
          id: grant.userId,
          name: grant.name ?? null,
          email: null,
          rawRole: grant.role,
          role: toRole(grant.role),
          organizationId: grant.organizationId ?? null,
          orgName: null,
        },
      };
      writeSession(base);
      const full = await refreshFromServer(base);
      setSession(full);
      // Right after signing in, as the Flutter console did: ask for push and register this
      // browser, so alerts reach it with the tab closed. Never blocks the sign-in.
      if (full) void registerForPush(client, { prompt: true });
    },
    [refreshFromServer, client],
  );

  const signOut = useCallback(async () => {
    // Retire this browser's push token first, while the session can still say whose it is.
    await unregisterPush(client);
    try {
      await api.logout(client);
    } catch {
      /* the local session goes regardless */
    }
    clearSession();
    setSession(null);
    setViewAsState(null);
  }, [client]);

  const setViewAs = useCallback(
    (r: Role | null, facility?: { id: string; name: string | null } | null) => {
      if (r === null) {
        setViewAsState(null);
        setViewAsFacility(null);
        // The server ends and logs it; the console is back to your own role regardless.
        void api.viewAsEnd(client).catch(() => undefined);
        return;
      }
      api
        .viewAsStart(client, { body: { role: r, organizationId: facility?.id ?? null } })
        .then((res) => {
          // The server says whether the viewed building is a demo building.
          setViewAsFacility(facility ? { ...facility, demoBuilding: res?.session?.demoBuilding === true } : null);
          setViewAsState(r);
        })
        .catch((err: { status?: number }) => {
          // Refused (not a Super Admin, or signed out): stay on your own role. A server
          // without View as support yet (404) still gets the read-only client view.
          if (err?.status === 404) setViewAsState(r);
        });
    },
    [client],
  );

  // Viewing a building as one of its roles: pages read the building from the session, so
  // they get the viewed one (the server answers for it too, from the View as session).
  // The token and the real role never change.
  const shownFacility = viewAs ? viewAsFacility : null;
  const shown = useMemo(
    () =>
      session && shownFacility
        ? { ...session, user: { ...session.user, organizationId: shownFacility.id, orgName: shownFacility.name, demoBuilding: shownFacility.demoBuilding === true } }
        : session && viewAs
          ? // Viewing as with no building picked (or a team role): never a demo building scope.
            { ...session, user: { ...session.user, demoBuilding: false } }
          : session,
    [session, shownFacility, viewAs],
  );
  const value = useMemo(
    () => ({ session: shown, checking, client, signIn, signOut, viewAs, setViewAs, viewAsFacility: shownFacility }),
    [shown, checking, client, signIn, signOut, viewAs, setViewAs, shownFacility],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * The role the console renders (your own, or the one you are viewing as), your real
 * role, and the viewed role when viewing as one. Pages read `role` from here, never the
 * session directly, so View as reaches every page.
 */
export function useConsoleRole(): { role: Role | null; realRole: Role | null; viewingAs: Role | null } {
  const { session, viewAs } = useSession();
  const realRole = session?.user.role ?? null;
  return { role: viewAs ?? realRole, realRole, viewingAs: viewAs };
}

/**
 * The role on screen (View as, or the signed-in role), or null outside a session (a part
 * rendered on its own). Never throws, for helpers every page draws (useBuildLabels).
 */
export function useOptionalConsoleRole(): Role | null {
  const v = useContext(Ctx);
  if (!v) return null;
  return v.viewAs ?? v.session?.user.role ?? null;
}

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession must be used inside SessionProvider');
  return v;
}
